import { Description, Tune } from '@mui/icons-material';
import {
  Box,
  DialogActions,
  DialogContent,
  Grid,
  Typography,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import {
  deleteLegalCollection,
  updateLegalCollection,
} from 'lib/api/legalCollections';
import {
  LegalCollection,
  UpdateLegalCollectionInput,
} from 'lib/api/legalCollections/types';
import {
  DateInput,
  MoneyInput,
  SelectInput,
  TextInput,
} from 'lib/common/Inputs';
import CustomButton from 'lib/designSystem/Button';
import TableCard from 'lib/designSystem/Card/TableCard';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import SmallHeaderWithIcon from 'lib/designSystem/SmallHeaderWithIcon';
import {
  formatDate,
  formatMoney,
  showAlert,
  validaRut,
  validatePermissions,
} from 'lib/functions/utils';
import { SummaryRow } from 'pages/Finance/Components/Financing/common/Summary';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

interface LegalCollectionDetailsProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  legalCollection: LegalCollection;
}

const STATUS_OPTIONS = [
  { value: 'Cobranza Judicial', label: 'Cobranza Judicial' },
  { value: 'Recuperado', label: 'Recuperado' },
  { value: 'Incobrable', label: 'Incobrable' },
];

const STATUS_LABELS = {
  'Cobranza Judicial': 'LEGAL_COLLECTION',
  Recuperado: 'RECOVERED',
  Incobrable: 'UNCOLLECTIBLE',
};

const LegalCollectionDetails: React.FC<LegalCollectionDetailsProps> = ({
  open,
  setOpen,
  legalCollection,
}) => {
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const canDelete = validatePermissions(
    user,
    'financing_legal_collection_delete',
  );
  const canEdit = validatePermissions(user, 'financing_legal_collection_edit');
  const dispatch = useDispatch();
  const [isEditMode, setIsEditMode] = useState(false);
  const [status, setStatus] = useState<string>(legalCollection.status);
  const [startDate, setStartDate] = useState<Date>(
    new Date(legalCollection.startDate),
  );
  const [endDate, setEndDate] = useState<Date | null>(
    legalCollection.endDate ? new Date(legalCollection.endDate) : null,
  );
  const [recoveredAmount, setRecoveredAmount] = useState<number | null>(
    legalCollection.recoveredAmount,
  );
  const [lawyerIdentifier, setLawyerIdentifier] = useState<string>(
    legalCollection.lawyerIdentifier || '',
  );
  const [lawyerName, setLawyerName] = useState<string>(
    legalCollection.lawyerName || '',
  );
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const {
    mutate: updateLegalCollectionMutation,
    isPending: isUpdatingLegalCollection,
  } = updateLegalCollection(dispatch);

  const {
    mutate: deleteLegalCollectionMutation,
    isPending: isDeletingLegalCollection,
  } = deleteLegalCollection(dispatch);

  // Reset form when legalCollection changes
  useEffect(() => {
    setStatus(legalCollection.status);
    setStartDate(new Date(legalCollection.startDate));
    setEndDate(
      legalCollection.endDate ? new Date(legalCollection.endDate) : null,
    );
    setRecoveredAmount(legalCollection.recoveredAmount);
    setLawyerIdentifier(legalCollection.lawyerIdentifier || '');
    setLawyerName(legalCollection.lawyerName || '');
    setIsEditMode(false);
  }, [legalCollection]);

  const showEndDate = status === 'Recuperado' || status === 'Incobrable';
  const showRecoveredAmount = status === 'Recuperado';

  const validParams = () => {
    if (!startDate) {
      showAlert(
        dispatch,
        'error',
        'Debes seleccionar una fecha de inicio de proceso',
      );
      return false;
    }
    if (showEndDate && !endDate) {
      showAlert(dispatch, 'error', 'Debes seleccionar una fecha de término');
      return false;
    }
    if (showRecoveredAmount && recoveredAmount === null) {
      showAlert(dispatch, 'error', 'Debes ingresar el monto recuperado');
      return false;
    }
    if (lawyerIdentifier && !validaRut(lawyerIdentifier)) {
      showAlert(dispatch, 'error', 'Rut abogado/firma inválido');
      return false;
    }
    return true;
  };

  const handleChangeStatus = (value: string | null) => {
    setStatus(value || '');
    if (value === 'Cobranza Judicial') {
      setEndDate(null);
      setRecoveredAmount(null);
    } else if (value === 'Recuperado') {
      setEndDate(new Date(new Date().setUTCHours(12, 0, 0, 0)));
      setRecoveredAmount(null);
    } else if (value === 'Incobrable') {
      setEndDate(new Date(new Date().setUTCHours(12, 0, 0, 0)));
      setRecoveredAmount(null);
    }
  };

  const handleUpdate = async () => {
    if (!validParams()) return;

    const updateData: UpdateLegalCollectionInput = {
      status: STATUS_LABELS[status as keyof typeof STATUS_LABELS],
      startDate: new Date(new Date(startDate).setUTCHours(12, 0, 0, 0)),
      lawyerIdentifier: lawyerIdentifier === '' ? undefined : lawyerIdentifier,
      lawyerName: lawyerName === '' ? undefined : lawyerName,
    };

    if (showEndDate && endDate) {
      updateData.endDate = new Date(new Date(endDate).setUTCHours(12, 0, 0, 0));
    } else if (!showEndDate) {
      updateData.endDate = undefined;
    }

    if (showRecoveredAmount) {
      updateData.recoveredAmount = recoveredAmount || undefined;
    } else {
      updateData.recoveredAmount = undefined;
    }

    updateLegalCollectionMutation(
      {
        id: legalCollection.id,
        data: updateData,
      },
      {
        onSuccess: () => {
          showAlert(
            dispatch,
            'success',
            'Proceso de cobranza judicial actualizado exitosamente',
          );
          setIsEditMode(false);
        },
        onError: () => {
          showAlert(
            dispatch,
            'error',
            'Ha habido un error al actualizar el proceso de cobranza judicial',
          );
        },
      },
    );
  };

  const handleDelete = () => {
    deleteLegalCollectionMutation(legalCollection.id, {
      onSuccess: () => {
        showAlert(
          dispatch,
          'success',
          'Proceso de cobranza judicial eliminado exitosamente',
        );
        setOpen(false);
      },
      onError: () => {
        showAlert(
          dispatch,
          'error',
          'Ha habido un error al eliminar el proceso de cobranza judicial',
        );
      },
    });
  };

  const handleCancel = () => {
    // Reset to original values
    setStatus(legalCollection.status);
    setStartDate(new Date(legalCollection.startDate));
    setEndDate(
      legalCollection.endDate ? new Date(legalCollection.endDate) : null,
    );
    setRecoveredAmount(legalCollection.recoveredAmount);
    setLawyerIdentifier(legalCollection.lawyerIdentifier || '');
    setLawyerName(legalCollection.lawyerName || '');
    setIsEditMode(false);
  };

  const isLoading = isUpdatingLegalCollection || isDeletingLegalCollection;

  return (
    <div>
      <Dialog open={open} maxWidth="lg" fullWidth>
        <DialogTitle>
          {isEditMode
            ? 'Editar proceso de cobranza judicial'
            : 'Detalles del proceso de cobranza judicial'}
        </DialogTitle>
        <DialogContent>
          {isLoading ? (
            <CenteredCircularProgress type="dialog" />
          ) : (
            <Box>
              <TableCard
                size="small"
                enabledEffects={false}
                sx={{
                  boxShadow: 'none',
                  background: 'transparent',
                  border: 'none',
                }}
              >
                <SmallHeaderWithIcon
                  icon={<Description fontSize="small" />}
                  title="Datos del documento"
                />
                <Grid container spacing={0} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Box sx={{ pr: 2 }}>
                      <SummaryRow
                        label="Folio"
                        value={legalCollection.folio}
                        labelVariant="body2"
                        valueVariant="body2"
                      />
                      <SummaryRow
                        label="Emisor"
                        value={legalCollection.businessName}
                        labelVariant="body2"
                        valueVariant="body2"
                        valueMaxLength={40}
                      />
                      <SummaryRow
                        label="Rut emisor"
                        value={legalCollection.businessIdentifier}
                        labelVariant="body2"
                        valueVariant="body2"
                        showBorder={false}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        pl: 2,
                        borderLeft: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <SummaryRow
                        label="Monto documento"
                        value={formatMoney(legalCollection.amount)}
                        labelVariant="body2"
                        valueVariant="body2"
                      />
                      <SummaryRow
                        label="Deudor"
                        value={legalCollection.stakeholderName}
                        labelVariant="body2"
                        valueVariant="body2"
                        valueMaxLength={40}
                      />
                      <SummaryRow
                        label="Rut deudor"
                        value={legalCollection.stakeholderIdentifier}
                        labelVariant="body2"
                        valueVariant="body2"
                      />
                    </Box>
                  </Grid>
                </Grid>

                <SmallHeaderWithIcon
                  icon={<Tune fontSize="small" />}
                  title="Parámetros"
                />
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <SelectInput
                      label="Estado"
                      value={status}
                      onChange={handleChangeStatus}
                      options={STATUS_OPTIONS}
                      disabled={isLoading || !isEditMode}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <MoneyInput
                      label="Monto recuperado"
                      value={recoveredAmount}
                      disabled={
                        isLoading || !isEditMode || !showRecoveredAmount
                      }
                      onChange={(value) => setRecoveredAmount(value)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DateInput
                      label="Fecha de inicio"
                      value={formatDate(startDate)}
                      disabled={isLoading || !isEditMode}
                      onChange={(value) => setStartDate(new Date(value))}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DateInput
                      label="Fecha de término"
                      value={endDate ? formatDate(endDate) : ''}
                      disabled={isLoading || !isEditMode || !showEndDate}
                      onChange={(value) => setEndDate(new Date(value))}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextInput
                      label="Rut abogado/firma (Opcional)"
                      value={lawyerIdentifier}
                      disabled={isLoading || !isEditMode}
                      onChange={(value) => setLawyerIdentifier(value || '')}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextInput
                      label="Nombre abogado/firma (Opcional)"
                      value={lawyerName}
                      disabled={isLoading || !isEditMode}
                      onChange={(value) => setLawyerName(value || '')}
                    />
                  </Grid>
                </Grid>
              </TableCard>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {isEditMode ? (
            <>
              <CustomButton
                color="secondary"
                onClick={handleCancel}
                disabled={isLoading}
              >
                Cancelar
              </CustomButton>
              <CustomButton
                color="success"
                onClick={handleUpdate}
                disabled={isLoading}
              >
                Guardar
              </CustomButton>
            </>
          ) : (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 10,
                width: '100%',
                marginLeft: 10,
                marginRight: 10,
              }}
            >
              {canDelete ? (
                <Typography
                  color="error"
                  variant="body1"
                  fontWeight="bold"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setOpenDeleteDialog(true)}
                >
                  Eliminar
                </Typography>
              ) : (
                <div />
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                <CustomButton
                  color="warning"
                  onClick={() => setIsEditMode(true)}
                  disabled={isLoading || !canEdit}
                >
                  Editar
                </CustomButton>
                <CustomButton
                  color="secondary"
                  onClick={() => setOpen(false)}
                  disabled={isLoading}
                >
                  Cerrar
                </CustomButton>
              </div>
            </div>
          )}
        </DialogActions>
      </Dialog>
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Eliminar proceso de cobranza judicial</DialogTitle>
        <DialogContent>
          {isLoading ? (
            <div style={{ minHeight: 100 }}>
              <CenteredCircularProgress type="dialog" />
            </div>
          ) : (
            <Typography>
              ¿Estás seguro de que deseas eliminar este proceso de cobranza
              judicial?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <CustomButton
            color="secondary"
            onClick={() => setOpenDeleteDialog(false)}
          >
            Cancelar
          </CustomButton>
          <CustomButton color="error" onClick={handleDelete}>
            Eliminar
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default LegalCollectionDetails;
